import React, { useState, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { GoogleGenAI } from "@google/genai";
import { useLocalization } from '../../../../hooks/useLocalization';
import DestinationEditor from './DestinationEditor';
import type { DestinationContent } from '../../../../types';
import { triggerUpdateEvent } from '../../../../services/dataService';
import { enrichDestinationContent } from '../../../../services/contentEnricherService';

// Initialize AI
const apiKey = import.meta.env.VITE_GEMINI_API_KEY || import.meta.env.VITE_API_KEY || process.env.GEMINI_API_KEY;
const ai = apiKey ? new GoogleGenAI({ apiKey }) : null;

// Helper function to safely extract localized text from potentially localized objects
const getLocalizedText = (value: any, lang: 'en' | 'sv' = 'en'): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        // Handle {en, sv} translation objects
        if (value[lang] !== undefined) return String(value[lang]);
        if (value.en !== undefined) return String(value.en);
        if (value.sv !== undefined) return String(value.sv);
        // Fallback: return first string value found
        for (const key of Object.keys(value)) {
            if (typeof value[key] === 'string') return value[key];
        }
    }
    return String(value);
};

// Defensive helper: ensures any value is a safe string for React rendering
const safeStr = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
        // If it's a localized object, extract appropriate text
        if ('en' in value || 'sv' in value) {
            return String(value.en || value.sv || '');
        }
        // Don't render plain objects
        return '';
    }
    return String(value);
};

// Version marker for deployment verification - ALWAYS update when fixing rendering bugs
const COMPONENT_VERSION = 'v11-backbuttonfix-2025-01-13';

// Error Boundary Component to catch and display render errors
class DestinationErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback?: React.ReactNode },
    { hasError: boolean; error: Error | null; errorInfo: React.ErrorInfo | null }
> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('DestinationErrorBoundary caught error:', error);
        console.error('Error Info:', errorInfo);
        console.error('Component Stack:', errorInfo.componentStack);
        this.setState({ errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl m-4">
                    <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">
                        ⚠️ Render Error in Destinations (v8)
                    </h2>
                    <p className="text-red-700 dark:text-red-300 mb-2">
                        <strong>Error:</strong> {this.state.error?.message}
                    </p>
                    <details className="mt-4">
                        <summary className="cursor-pointer text-red-600 dark:text-red-400 font-medium">
                            Show Stack Trace
                        </summary>
                        <pre className="mt-2 p-4 bg-red-100 dark:bg-red-900/40 rounded text-xs overflow-auto max-h-60">
                            {this.state.errorInfo?.componentStack}
                        </pre>
                    </details>
                    <button
                        onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        Try Again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// CRITICAL: Sanitize ENTIRE destination object to prevent ANY localized objects from reaching render
// This is called on ALL destination data before storing in state
const sanitizeDestination = (dest: any, language: 'en' | 'sv' = 'en'): any => {
    if (!dest) return null;

    // Helper to extract text from potentially localized value
    const txt = (val: any): string => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'number' || typeof val === 'boolean') return String(val);
        if (typeof val === 'object' && ('en' in val || 'sv' in val)) {
            return String(val[language] || val.en || val.sv || '');
        }
        return '';
    };

    return {
        id: String(dest.id || dest._id || ''),
        name: txt(dest.name) || txt(dest.title) || 'Unnamed',
        country: txt(dest.country) || '',
        description: txt(dest.description) || '',
        imageUrl: String(dest.landingPage?.heroImage || dest.heroImage || dest.imageUrl || dest.image || 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800'),
        avgPrice: typeof dest.avgPrice === 'number' ? dest.avgPrice : 850,
        currency: String(dest.currency || 'EUR'),
        bestSeason: String(dest.bestSeason || 'summer') as any,
        rating: typeof dest.rating === 'number' ? dest.rating : 4.5,
        tags: Array.isArray(dest.tags) ? dest.tags.map((t: any) => txt(t)).filter(Boolean) as string[] : [],
        climate: txt(dest.climate) || 'Mediterranean',
        highlights: Array.isArray(dest.highlights) ? dest.highlights.map((h: any) => txt(h)).filter(Boolean) as string[] : [],
        travelTips: Array.isArray(dest.travelTips) ? dest.travelTips.map((t: any) => txt(t)).filter(Boolean) as string[] : [],
        bookings: typeof dest.bookings === 'number' ? dest.bookings : 0,
        featured: Boolean(dest.featured)
    };
};

// Helper to map API response (MongoDB Destination) to DestinationContent
const mapApiToContent = (dest: any): DestinationContent => {
    return {
        _id: dest._id,
        id: dest._id, // Use MongoDB _id as the primary identifier
        destinationId: dest.tourvisioId || '',
        slug: dest.slug || '',
        title: dest.name || '',
        titleSv: dest.description?.sv ? '' : '', // Will be populated from DB if available
        imageUrl: dest.landingPage?.heroImage || dest.heroImage || dest.imageUrl || dest.coverImage || '',
        heroImage: dest.landingPage?.heroImage || dest.heroImage || '',
        price: dest.price || '',
        type: dest.type?.toString() || '',
        isActive: dest.isActive !== false,
        isFeatured: dest.featured || false,
        landingPage: dest.landingPage || { description: { en: '', sv: '' } },
        richContent: dest.richContent,
        gallery: dest.gallery || [],
        layout: dest.layout || [],
        weather: dest.weather,
        sections: dest.sections || {},
        sectionConfig: dest.sectionConfig || [],
        useCacheData: dest.useCacheData,
        // Hybrid architecture fields
        dataSource: dest.dataSource || 'manual',
        lockContent: dest.lockContent || false,
        // Airport info
        airportInfo: dest.airportInfo
    } as DestinationContent;
};

// Data source badge colors and labels
const DATA_SOURCE_CONFIG: Record<string, { label: string; labelSv: string; color: string; icon: string }> = {
    seed: { label: 'Seed Data', labelSv: 'Startdata', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', icon: 'database' },
    manual: { label: 'Manual', labelSv: 'Manuell', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: 'edit' },
    tourvisio_api: { label: 'Tourvisio API', labelSv: 'Tourvisio API', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30', icon: 'api' },
    ai_enriched: { label: 'AI Enriched', labelSv: 'AI-berikad', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30', icon: 'auto_awesome' }
};

// Helper to map shared DestinationContent to local Destination interface
const mapContentToDestination = (content: DestinationContent, language: 'en' | 'sv'): Destination => {
    // Parse price string "Fr. 5500 kr" -> 5500
    let price = 0;
    if (content.price) {
        const match = String(content.price).match(/\d+/);
        if (match) price = parseInt(match[0], 10);
    }

    return sanitizeDestination({
        id: content._id || content.id,
        name: content.title,
        country: '', // Content doesn't have explicit country field
        description: content.landingPage?.description,
        imageUrl: content.landingPage?.heroImage || content.heroImage || content.imageUrl,
        avgPrice: price,
        currency: 'SEK',
        bestSeason: 'summer',
        rating: 4.5,
        tags: ['Travel', 'Explore'],
        highlights: content.sections?.thingsToDo?.map(t => t.title) || [],
        featured: content.isFeatured
    }, language);
};

// Types
interface Destination {
    id: string;
    name: string;
    country: string;
    description: string;
    imageUrl: string;
    avgPrice: number;
    currency: string;
    bestSeason: 'spring' | 'summer' | 'autumn' | 'winter' | 'all-year';
    rating: number;
    tags: string[];
    climate: string;
    highlights: string[];
    travelTips: string[];
    bookings: number;
    featured: boolean;
}

interface DestinationsContainerProps {
    currentUser?: any;
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
}

// Mock destinations data with Lumi Tours theme - Keeping as fallback
const MOCK_DESTINATIONS: Destination[] = [
    {
        id: 'antalya',
        name: 'Antalya',
        country: 'Turkey',
        description: 'The gem of the Turkish Riviera with stunning beaches, ancient ruins, and world-class resorts.',
        imageUrl: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800',
        avgPrice: 850,
        currency: 'EUR',
        bestSeason: 'summer',
        rating: 4.8,
        tags: ['Beach', 'All-Inclusive', 'Family', 'Culture'],
        climate: 'Mediterranean - Hot summers, mild winters',
        highlights: ['Kaleiçi Old Town', 'Düden Waterfalls', 'Aspendos Theatre', 'Konyaaltı Beach'],
        travelTips: ['Best time: May-October', 'Haggle at bazaars', 'Try local kebabs'],
        bookings: 1250,
        featured: true
    },
    // ... (rest of mock data can be assumed or re-added if connection fails)
];

// Translations
const translations = {
    en: {
        title: 'Destinations',
        subtitle: 'Manage and explore travel destinations',
        search: 'Search destinations...',
        allDestinations: 'All Destinations',
        featured: 'Featured',
        popular: 'Most Popular',
        addNew: 'Add Destination',
        filter: 'Filter',
        sortBy: 'Sort by',
        rating: 'Rating',
        price: 'Price',
        bookings: 'Bookings',
        viewDetails: 'View Details',
        edit: 'Edit',
        generateAI: 'Generate AI Content',
        bestSeason: 'Best Season',
        avgPrice: 'Avg. Price',
        from: 'from',
        perPerson: 'per person',
        highlights: 'Highlights',
        travelTips: 'Travel Tips',
        climate: 'Climate',
        totalBookings: 'Total Bookings',
        close: 'Close',
        save: 'Save Changes',
        generating: 'Generating...',
        aiGenerated: 'AI Generated Content',
        spring: 'Spring',
        summer: 'Summer',
        autumn: 'Autumn',
        winter: 'Winter',
        'all-year': 'All Year',
        featuredDestination: 'Featured Destination',
        exploreNow: 'Explore Now',
        discoverWorld: 'Discover the World',
        heroSubtitle: 'Explore our handpicked destinations for unforgettable travel experiences',
        noApiKey: 'No AI API Key configured. Please check your .env file.',
        aiError: 'Failed to generate content. Please try again.'
    },
    sv: {
        title: 'Destinationer',
        subtitle: 'Hantera och utforska resedestinationer',
        search: 'Sök destinationer...',
        allDestinations: 'Alla Destinationer',
        featured: 'Utvalda',
        popular: 'Mest Populära',
        addNew: 'Lägg till Destination',
        filter: 'Filtrera',
        sortBy: 'Sortera efter',
        rating: 'Betyg',
        price: 'Pris',
        bookings: 'Bokningar',
        viewDetails: 'Visa Detaljer',
        edit: 'Redigera',
        generateAI: 'Generera AI-innehåll',
        bestSeason: 'Bästa Säsong',
        avgPrice: 'Snittpris',
        from: 'från',
        perPerson: 'per person',
        highlights: 'Höjdpunkter',
        travelTips: 'Resetips',
        climate: 'Klimat',
        totalBookings: 'Totala Bokningar',
        close: 'Stäng',
        save: 'Spara Ändringar',
        generating: 'Genererar...',
        aiGenerated: 'AI-genererat Innehåll',
        spring: 'Vår',
        summer: 'Sommar',
        autumn: 'Höst',
        winter: 'Vinter',
        'all-year': 'Hela året',
        featuredDestination: 'Utvald Destination',
        exploreNow: 'Utforska Nu',
        discoverWorld: 'Upptäck Världen',
        heroSubtitle: 'Utforska våra handplockade destinationer för oförglömliga reseupplevelser',
        noApiKey: 'Ingen AI API-nyckel konfigurerad. Kontrollera din .env-fil.',
        aiError: 'Misslyckades med att generera innehåll. Försök igen.'
    }
};

const DestinationsContainer: React.FC<DestinationsContainerProps> = ({
    currentUser,
    theme = 'light',
    language = 'en'
}) => {
    // CRITICAL: Sanitize mock data on initial mount to prevent any localized objects in rendering
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState<'all' | 'featured' | 'popular'>('all');
    const [sortBy, setSortBy] = useState<'rating' | 'price' | 'bookings'>('bookings');
    const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [aiContent, setAiContent] = useState<string>('');
    const [editingDestination, setEditingDestination] = useState<DestinationContent | null>(null);
    const [fullDestinations, setFullDestinations] = useState<DestinationContent[]>([]);
    const [enrichingId, setEnrichingId] = useState<string | null>(null);
    const [dataSourceFilter, setDataSourceFilter] = useState<'all' | 'seed' | 'manual' | 'tourvisio_api' | 'ai_enriched'>('all');
    const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [isSeeding, setIsSeeding] = useState(false);

    // Video & Blog states
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [blogSuggestions, setBlogSuggestions] = useState<any[]>([]);
    const [generatingBlogFor, setGeneratingBlogFor] = useState<string | null>(null);
    const [blogPreview, setBlogPreview] = useState<any | null>(null);
    const [savingBlog, setSavingBlog] = useState(false);
    const [blogMessage, setBlogMessage] = useState<{ type: string, text: string } | null>(null);

    // Load data from backend API (MongoDB)
    const loadDestinations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/destinations');
            const data = await res.json();
            if (data.success && data.destinations && data.destinations.length > 0) {
                console.log('[DB-FIRST] Loaded destinations from API:', data.destinations.length);
                const contentData = data.destinations.map(mapApiToContent);
                setFullDestinations(contentData);
                const mapped = contentData.map((d: DestinationContent) => mapContentToDestination(d, language));
                setDestinations(mapped);
            } else {
                console.log('[DB-FIRST] No destinations in database');
                setFullDestinations([]);
                setDestinations([]);
            }
        } catch (error) {
            console.error('[DB-FIRST] Error loading destinations from API:', error);
            setFullDestinations([]);
            setDestinations([]);
        } finally {
            setLoading(false);
        }
    }, [language]);

    // Load YouTube URL from settings
    const loadVideoUrl = useCallback(async () => {
        try {
            const res = await fetch('/api/settings/public');
            const data = await res.json();
            if (data.success && data.settings) {
                const videoSetting = data.settings.find?.((s: any) => s.key === 'DESTINATION_VIDEO_URL');
                if (videoSetting?.value) setYoutubeUrl(videoSetting.value);
            }
        } catch (err) {
            console.warn('[ANTIGRAVITY] Failed to load video URL from settings');
        }
    }, []);

    useEffect(() => {
        console.log(`[ANTIGRAVITY] DestinationsContainer ${COMPONENT_VERSION} MOUNTED`);
        loadDestinations();
        loadVideoUrl();
    }, [loadDestinations, loadVideoUrl]);

    const isDark = theme === 'dark';
    const t = translations[language] || translations.en;


    // Currency formatting based on language
    const formatPrice = (price: number) => {
        if (language === 'sv') {
            return `${price.toLocaleString('sv-SE')} kr`;
        }
        return `€${price.toLocaleString('en-US')}`;
    };

    // Filter and sort destinations
    const filteredDestinations = useMemo(() => {
        let result = [...destinations];

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(d =>
                d.name.toLowerCase().includes(query) ||
                d.country.toLowerCase().includes(query) ||
                d.tags.some(tag => tag.toLowerCase().includes(query))
            );
        }

        // Data source filter
        if (dataSourceFilter !== 'all') {
            const matchIds = fullDestinations
                .filter((fd: any) => fd.dataSource === dataSourceFilter)
                .map((fd: any) => fd.id);
            result = result.filter(d => matchIds.includes(d.id));
        }

        // Active/Inactive status filter
        if (activeFilter !== 'all') {
            const isActiveTarget = activeFilter === 'active';
            const activeIds = fullDestinations
                .filter((fd: any) => fd.isActive === isActiveTarget)
                .map((fd: any) => fd.id);
            result = result.filter(d => activeIds.includes(d.id));
        }

        // Category filter
        if (selectedFilter === 'featured') {
            result = result.filter(d => d.featured);
        } else if (selectedFilter === 'popular') {
            result = result.sort((a, b) => b.bookings - a.bookings).slice(0, 4);
        }

        // Sort
        result.sort((a, b) => {
            if (sortBy === 'rating') return b.rating - a.rating;
            if (sortBy === 'price') return a.avgPrice - b.avgPrice;
            return b.bookings - a.bookings;
        });

        return result;
    }, [destinations, searchQuery, selectedFilter, sortBy, dataSourceFilter, activeFilter, fullDestinations]);

    // Seed preloaded data to MongoDB
    const handleSeedPreloaded = async () => {
        if (isSeeding) return;
        const confirmMsg = language === 'sv'
            ? 'Hardcoded verileri MongoDB\'ye yüklemek istediğinize emin misiniz? Mevcut veriler korunacak.'
            : 'Seed preloaded data to MongoDB? Existing data will be preserved.';
        if (!confirm(confirmMsg)) return;

        setIsSeeding(true);
        try {
            const res = await fetch('/api/destinations/seed-preloaded', { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                alert(language === 'sv'
                    ? `✅ Seed tamamlandı: ${data.created} oluşturuldu, ${data.updated} güncellendi, ${data.skipped} atlandı`
                    : `✅ Seed complete: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
                loadDestinations();
            } else {
                alert(`❌ ${data.error}`);
            }
        } catch (err: any) {
            alert(`❌ Seed failed: ${err.message}`);
        } finally {
            setIsSeeding(false);
        }
    };

    // Toggle lockContent for a destination
    const handleToggleLock = async (dest: Destination) => {
        const fullDest = fullDestinations.find((d: any) => d.id === dest.id) as any;
        if (!fullDest) return;
        const newLock = !fullDest.lockContent;
        try {
            const res = await fetch(`/api/destinations/${fullDest._id || fullDest.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lockContent: newLock })
            });
            const data = await res.json();
            if (data.success) {
                loadDestinations();
            }
        } catch (err) {
            console.error('Failed to toggle lock:', err);
        }
    };

    const featuredDestination = destinations.find(d => d.featured) || destinations[0];

    // Extract YouTube video ID for embed
    const getYoutubeEmbedUrl = (url: string) => {
        if (!url) return '';
        const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([a-zA-Z0-9_-]{11})/);
        return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0` : url;
    };

    // Handle category button click
    const handleCategoryClick = async (categoryId: string) => {
        if (activeCategory === categoryId) {
            setActiveCategory(null);
            setBlogSuggestions([]);
            setBlogPreview(null);
            return;
        }
        setActiveCategory(categoryId);
        setBlogSuggestions([]);
        setBlogPreview(null);
        setBlogMessage(null);
    };

    // Generate blog content for a destination + category
    const handleGenerateBlog = async (destId: string) => {
        if (!activeCategory || generatingBlogFor) return;
        setGeneratingBlogFor(destId);
        setBlogPreview(null);
        setBlogMessage(null);
        try {
            const res = await fetch(`/api/destination-blog/${destId}/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ category: activeCategory })
            });
            const data = await res.json();
            if (data.success) {
                setBlogPreview(data.preview);
            } else {
                setBlogMessage({ type: 'error', text: data.error || 'Generation failed' });
            }
        } catch (err: any) {
            setBlogMessage({ type: 'error', text: err.message });
        } finally {
            setGeneratingBlogFor(null);
        }
    };

    // Save generated blog post
    const handleSaveBlogPost = async (status: 'draft' | 'published' = 'draft') => {
        if (!blogPreview) return;
        setSavingBlog(true);
        try {
            const res = await fetch(`/api/destination-blog/${blogPreview.destinationId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...blogPreview, status })
            });
            const data = await res.json();
            if (data.success) {
                setBlogMessage({ type: 'success', text: language === 'sv' ? `✅ Blogginlägg sparat som ${status}!` : `✅ Blog post saved as ${status}!` });
                setBlogPreview(null);
            } else {
                setBlogMessage({ type: 'error', text: data.error });
            }
        } catch (err: any) {
            setBlogMessage({ type: 'error', text: err.message });
        } finally {
            setSavingBlog(false);
        }
    };

    // Show loading state
    if (loading && destinations.length === 0) {
        return (
            <div className={`h-full flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
                    <p className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {language === 'sv' ? 'Laddar destinationer...' : 'Loading destinations...'}
                    </p>
                </div>
            </div>
        );
    }

    // Real AI content generation using Google GenAI
    const generateAIContent = async (dest: Destination) => {
        if (!ai) {
            alert(t.noApiKey);
            return;
        }

        setIsGeneratingAI(true);
        setAiContent('');

        try {
            const systemPrompt = `You are a professional travel guide editor. Create an engaging travel guide for ${dest.name}, ${dest.country}.
            Format it with Markdown.
            Include:
            - A catchy headline
            - Why visit (short engaging description)
            - Top 5 things to do
            - Seasonal advice
            - Culinary tips (what to eat)
            - Language: ${language === 'sv' ? 'Swedish' : 'English'}
            `;

            console.log('[ANTIGRAVITY] Requesting AI content for:', dest.name);
            const result = await ai.models.generateContent({
                model: 'gemini-2.5-flash',
                contents: systemPrompt,
            });

            const responseText = result.text || '';
            console.log('[ANTIGRAVITY] AI response received');
            setAiContent(responseText);

        } catch (error) {
            console.error('AI Generation failed:', error);
            setAiContent(t.aiError);
        } finally {
            setIsGeneratingAI(false);
        }
    };

    const openModal = (dest: Destination) => {
        setSelectedDestination(dest);
        setShowModal(true);
        setAiContent('');
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedDestination(null);
        setAiContent('');
    };

    // Handle editing a full destination
    const handleEditDestination = (dest: Destination) => {
        // fullDestinations is loaded from API on mount
        const fullDest = fullDestinations.find(d => d.id === dest.id || d._id === dest.id);

        if (fullDest) {
            console.log(`[ANTIGRAVITY] Editing destination: ${fullDest.title} (id: ${fullDest.id})`);
            setEditingDestination(fullDest);
            setShowModal(false);
        } else {
            console.warn(`[ANTIGRAVITY] Destination not found by id: ${dest.id}, creating new`);
            // Create a new destination content from the basic destination
            const newDest: DestinationContent = {
                id: dest.id,
                destinationId: dest.id,
                title: dest.name,
                imageUrl: dest.imageUrl,
                price: `€${dest.avgPrice}`,
                type: 'Flight + Hotel',
                isActive: true,
                isFeatured: dest.featured,
                landingPage: {
                    description: { en: dest.description, sv: dest.description }
                },
                sections: {
                    thingsToDo: [],
                    attractions: [],
                    restaurants: [],
                    itineraries: [],
                    practicalInfo: undefined
                }
            };
            setEditingDestination(newDest);
            setShowModal(false);
        }
    };

    // Save destination handler — writes ONLY to backend MongoDB (single source of truth)
    const handleSaveDestination = async (updated: DestinationContent) => {
        try {
            const destId = updated._id || updated.slug || updated.destinationId || updated.id;
            const payload: Record<string, any> = {
                name: typeof updated.title === 'object' ? (updated.title as any).en || '' : updated.title,
                slug: updated.slug || updated.id,
                tourvisioId: updated.destinationId,
                isActive: updated.isActive !== false,
                featured: updated.isFeatured || false,
                landingPage: updated.landingPage,
                richContent: updated.richContent,
                weather: updated.weather,
                layout: updated.layout,
                sections: updated.sections,
                price: updated.price,
                sectionConfig: (updated as any).sectionConfig,
                useCacheData: (updated as any).useCacheData,
                airportInfo: updated.airportInfo
            };

            // Only include image fields if they have actual values (prevent clearing existing DB data)
            if (updated.imageUrl) payload.imageUrl = updated.imageUrl;
            if (updated.heroImage || updated.landingPage?.heroImage) {
                payload.heroImage = updated.heroImage || updated.landingPage?.heroImage;
            }
            if (updated.gallery && updated.gallery.length > 0) {
                payload.gallery = updated.gallery;
            }

            console.log('[DB-FIRST] Save payload keys:', Object.keys(payload));

            // Try PUT first (update existing), fall back to POST if 404
            const putRes = await fetch(`/api/destinations/${destId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const putResult = await putRes.json();

            if (!putRes.ok && putRes.status === 404) {
                // Destination doesn't exist in DB yet — create it
                const postRes = await fetch('/api/destinations', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const postResult = await postRes.json();
                if (postResult.success) {
                    console.log('[DB-FIRST] Destination created in backend:', destId);
                } else {
                    console.warn('[DB-FIRST] Backend POST failed:', postResult.error);
                    throw new Error(postResult.error || 'Failed to create destination');
                }
            } else if (putResult.success) {
                console.log('[DB-FIRST] Destination saved to backend:', destId);
            } else {
                console.warn('[DB-FIRST] Backend PUT failed:', putResult);
                throw new Error(putResult.error || 'Failed to save destination');
            }

            triggerUpdateEvent();
        } catch (err: any) {
            console.error('[DB-FIRST] Save failed:', err);
            throw err; // Re-throw so DestinationEditor shows error message
        }

        // Refresh from API
        await loadDestinations();
    };

    // Delete destination handler
    const handleDeleteDestination = async (dest: Destination) => {
        const confirmed = window.confirm(
            language === 'sv'
                ? `Är du säker på att du vill ta bort "${dest.name}"? Detta kan inte ångras.`
                : `Are you sure you want to delete "${dest.name}"? This cannot be undone.`
        );

        if (!confirmed) return;

        try {
            // Call backend API to delete
            const response = await fetch(`/api/destinations/${dest.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' }
            });

            const result = await response.json();

            if (result.success) {
                console.log(`[DB-FIRST] Destination "${dest.name}" deleted successfully`);
                triggerUpdateEvent();
                // Refresh from API
                await loadDestinations();
            } else {
                alert(language === 'sv'
                    ? `Kunde inte ta bort: ${result.error}`
                    : `Failed to delete: ${result.error}`
                );
            }
        } catch (error: any) {
            console.error('[ANTIGRAVITY] Delete error:', error);
            alert(language === 'sv'
                ? `Fel vid borttagning: ${error.message}`
                : `Delete error: ${error.message}`
            );
        }
    };

    // Toggle destination active status
    const handleToggleActive = async (dest: Destination) => {
        try {
            const response = await fetch(`/api/destinations/${dest.id}/toggle-active`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' }
            });

            const result = await response.json();

            if (result.success) {
                // Refresh the destinations list
                loadDestinations();
                console.log(`[ANTIGRAVITY] Destination "${dest.name}" ${result.isActive ? 'activated' : 'deactivated'}`);
            } else {
                alert(language === 'sv'
                    ? `Kunde inte ändra status: ${result.error}`
                    : `Failed to toggle status: ${result.error}`
                );
            }
        } catch (error: any) {
            console.error('[ANTIGRAVITY] Toggle active error:', error);
            alert(language === 'sv'
                ? `Fel vid statusändring: ${error.message}`
                : `Toggle error: ${error.message}`
            );
        }
    };

    // Season badge colors
    const getSeasonColor = (season: string) => {
        switch (season) {
            case 'spring': return 'bg-green-500/20 text-green-600 dark:text-green-400';
            case 'summer': return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400';
            case 'autumn': return 'bg-orange-500/20 text-orange-600 dark:text-orange-400';
            case 'winter': return 'bg-blue-500/20 text-blue-600 dark:text-blue-400';
            default: return 'bg-purple-500/20 text-purple-600 dark:text-purple-400';
        }
    };

    // Category cards for visitdubai.com style
    const categories = [
        { id: 'beach', icon: 'beach_access', label: language === 'sv' ? 'Stränder' : 'Beaches', color: 'from-cyan-500 to-blue-600' },
        { id: 'culture', icon: 'account_balance', label: language === 'sv' ? 'Kultur & Historia' : 'Culture & History', color: 'from-amber-500 to-orange-600' },
        { id: 'adventure', icon: 'terrain', label: language === 'sv' ? 'Äventyr' : 'Adventure', color: 'from-green-500 to-emerald-600' },
        { id: 'luxury', icon: 'diamond', label: language === 'sv' ? 'Lyx & Spa' : 'Luxury & Spa', color: 'from-purple-500 to-pink-600' },
        { id: 'food', icon: 'restaurant', label: language === 'sv' ? 'Mat & Dryck' : 'Food & Drink', color: 'from-rose-500 to-red-600' },
        { id: 'nightlife', icon: 'nightlife', label: language === 'sv' ? 'Nattliv' : 'Nightlife', color: 'from-indigo-500 to-violet-600' },
    ];

    if (editingDestination) {
        return (
            <div className="h-full bg-slate-50 dark:bg-slate-900">
                <DestinationEditor
                    destination={editingDestination}
                    onSave={handleSaveDestination}
                    onClose={() => setEditingDestination(null)}
                    theme={theme}
                />
            </div>
        );
    }

    return (
        <div className={`h-full overflow-y-auto ${isDark ? 'text-white bg-slate-900' : 'text-slate-900 bg-slate-50'}`}>
            {/* Full-width Hero - visitdubai style */}
            {featuredDestination && (
                <div className="relative h-[500px] overflow-hidden">
                    <img
                        src={featuredDestination.imageUrl || 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800'}
                        alt={featuredDestination.name || 'Featured Destination'}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                    {/* Hero Content */}
                    <div className="absolute bottom-24 left-0 right-0 p-8 md:p-12">
                        <div className="max-w-4xl">
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md text-white text-sm font-medium rounded-full mb-4 border border-white/30">
                                <span className="material-icons text-lg">auto_awesome</span>
                                {t.featuredDestination}
                            </span>
                            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                                {language === 'sv' ? 'Upplev Världen' : 'Experience the World'}
                            </h1>
                            <p className="text-white/90 text-xl max-w-2xl mb-6">
                                {language === 'sv'
                                    ? 'Utforska fantastiska destinationer, skapa oförglömliga minnen och upptäck din nästa drömresa.'
                                    : 'Explore stunning destinations, create unforgettable memories, and discover your next dream getaway.'}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <button
                                    onClick={() => openModal(featuredDestination)}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 font-bold rounded-full hover:bg-white/90 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
                                >
                                    <span className="material-icons">explore</span>
                                    {t.exploreNow}
                                </button>
                                <button
                                    onClick={() => setShowVideoModal(true)}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-white/20 backdrop-blur-md text-white font-bold rounded-full border border-white/30 hover:bg-white/30 transition-all"
                                >
                                    <span className="material-icons">play_circle</span>
                                    {language === 'sv' ? 'Se Video' : 'Watch Video'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Floating Stats */}
                    <div className="absolute top-8 right-8 hidden lg:flex flex-col gap-3">
                        <div className="px-5 py-3 bg-white/20 backdrop-blur-md rounded-2xl border border-white/30 text-white">
                            <p className="text-3xl font-bold">{destinations.length}+</p>
                            <p className="text-sm opacity-80">{language === 'sv' ? 'Destinationer' : 'Destinations'}</p>
                        </div>
                        <div className="px-5 py-3 bg-white/20 backdrop-blur-md rounded-2xl border border-white/30 text-white">
                            <p className="text-3xl font-bold">4.8★</p>
                            <p className="text-sm opacity-80">{language === 'sv' ? 'Snittbetyg' : 'Avg Rating'}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Category Exploration - visitdubai style */}
            <div className="px-4 md:px-8 -mt-16 relative z-10 mb-12">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => handleCategoryClick(cat.id)}
                            className={`group p-6 rounded-2xl bg-gradient-to-br ${cat.color} text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 ${activeCategory === cat.id ? 'ring-4 ring-white/60 scale-105' : ''}`}
                        >
                            <span className="material-icons text-4xl mb-3 group-hover:scale-110 transition-transform">{cat.icon}</span>
                            <p className="font-semibold text-sm">{cat.label}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Search & Filters */}
            <div className={`mx-4 md:mx-8 mb-8 p-6 rounded-3xl ${isDark ? 'bg-slate-800/80 backdrop-blur-md' : 'bg-white shadow-xl border border-slate-100'}`}>
                <div className="flex flex-wrap items-center gap-4">
                    {/* Search */}
                    <div className="flex-1 min-w-[280px]">
                        <div className={`flex items-center gap-3 px-5 py-3 rounded-2xl ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                            <span className="material-icons text-slate-400">search</span>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={t.search}
                                className={`w-full bg-transparent outline-none text-lg ${isDark ? 'text-white placeholder:text-slate-400' : 'text-slate-900 placeholder:text-slate-500'}`}
                            />
                        </div>
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-2">
                        {(['all', 'featured', 'popular'] as const).map(filter => (
                            <button
                                key={filter}
                                onClick={() => setSelectedFilter(filter)}
                                className={`px-5 py-2.5 text-sm font-semibold rounded-full transition-all ${selectedFilter === filter
                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                                    : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                            >
                                {filter === 'all' ? t.allDestinations : filter === 'featured' ? t.featured : t.popular}
                            </button>
                        ))}
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className={`px-5 py-2.5 rounded-full text-sm font-semibold outline-none cursor-pointer ${isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900'}`}
                    >
                        <option value="bookings">{t.sortBy}: {t.bookings}</option>
                        <option value="rating">{t.sortBy}: {t.rating}</option>
                        <option value="price">{t.sortBy}: {t.price}</option>
                    </select>

                    {/* Data Source Filter */}
                    <select
                        value={dataSourceFilter}
                        onChange={(e) => setDataSourceFilter(e.target.value as any)}
                        className={`px-4 py-2.5 rounded-full text-sm font-semibold outline-none cursor-pointer ${isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900'}`}
                    >
                        <option value="all">{language === 'sv' ? '📊 Alla Källor' : '📊 All Sources'}</option>
                        <option value="seed">🌱 {language === 'sv' ? 'Startdata' : 'Seed Data'}</option>
                        <option value="manual">✏️ {language === 'sv' ? 'Manuell' : 'Manual'}</option>
                        <option value="tourvisio_api">🔗 Tourvisio API</option>
                        <option value="ai_enriched">✨ AI</option>
                    </select>

                    {/* Active/Inactive Status Filter */}
                    <select
                        value={activeFilter}
                        onChange={(e) => setActiveFilter(e.target.value as any)}
                        className={`px-4 py-2.5 rounded-full text-sm font-semibold outline-none cursor-pointer ${isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900'}`}
                    >
                        <option value="all">{language === 'sv' ? '🔘 Alla Status' : '🔘 All Status'}</option>
                        <option value="active">{language === 'sv' ? '✅ Aktiva' : '✅ Active'}</option>
                        <option value="inactive">{language === 'sv' ? '⏸️ Inaktiva' : '⏸️ Inactive'}</option>
                    </select>

                    {/* Seed Button */}
                    <button
                        onClick={handleSeedPreloaded}
                        disabled={isSeeding}
                        className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${isSeeding
                            ? 'bg-emerald-400 text-white animate-pulse cursor-wait'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-emerald-500/30'
                            }`}
                        title={language === 'sv' ? 'Yükle startdata till MongoDB' : 'Seed preloaded data to MongoDB'}
                    >
                        <span className="material-icons text-sm">{isSeeding ? 'hourglass_empty' : 'cloud_upload'}</span>
                        {isSeeding ? (language === 'sv' ? 'Yükleniyor...' : 'Seeding...') : 'Seed'}
                    </button>

                    {/* Add Button */}
                    <button
                        onClick={() => {
                            console.log('[ANTIGRAVITY] Add Destination Clicked');
                            const newDest: DestinationContent = {
                                id: `new_${Date.now()}`,
                                destinationId: `new_${Date.now()}`,
                                title: '',
                                imageUrl: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800',
                                price: '€0',
                                type: 'Flight + Hotel',
                                isActive: true,
                                isFeatured: false,
                                landingPage: { description: { en: '', sv: '' } },
                                sections: {
                                    thingsToDo: [],
                                    attractions: [],
                                    restaurants: [],
                                    itineraries: [],
                                    practicalInfo: undefined
                                }
                            };
                            setEditingDestination(newDest);
                        }}
                        className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                    >
                        <span className="material-icons text-sm">add</span>
                        {t.addNew}
                    </button>
                </div>
            </div>

            {/* Destinations Grid */}
            <div className="mx-4 md:mx-8 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredDestinations.map(dest => (
                        <div
                            key={dest.id}
                            className={`group rounded-3xl overflow-hidden transition-all duration-300 ${isDark ? 'bg-slate-800 hover:bg-slate-700 shadow-lg shadow-black/20' : 'bg-white hover:shadow-xl shadow-sm border border-slate-100'}`}
                        >
                            {/* Image Container */}
                            <div className="relative h-64 overflow-hidden">
                                <img
                                    src={dest.imageUrl}
                                    alt={dest.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />
                                {/* Data Source Badge */}
                                {(() => {
                                    const fullDest = fullDestinations.find((d: any) => d.id === dest.id) as any;
                                    const source = fullDest?.dataSource || 'manual';
                                    const config = DATA_SOURCE_CONFIG[source] || DATA_SOURCE_CONFIG.manual;
                                    const isLocked = fullDest?.lockContent;
                                    return (
                                        <div className="absolute top-4 left-4 flex items-center gap-1.5">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-md border ${config.color}`}>
                                                <span className="material-icons" style={{ fontSize: '10px' }}>{config.icon}</span>
                                                {language === 'sv' ? config.labelSv : config.label}
                                            </span>
                                            {isLocked && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-md bg-red-500/20 text-red-300 border border-red-500/30" title="Content locked">
                                                    <span className="material-icons" style={{ fontSize: '10px' }}>lock</span>
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Toggle featured logic would need to update dataService
                                    }}
                                    className={`absolute top-4 right-4 p-2 rounded-full backdrop-blur-md transition-all ${dest.featured
                                        ? 'bg-yellow-400 text-white shadow-lg shadow-yellow-400/30'
                                        : 'bg-white/10 text-white hover:bg-white/30'
                                        }`}
                                >
                                    <span className="material-icons text-sm">{dest.featured ? 'star' : 'star_border'}</span>
                                </button>
                                <div className="absolute bottom-4 left-4 text-white">
                                    <h3 className="text-2xl font-bold mb-1">{dest.name}</h3>
                                    <p className="flex items-center gap-1 text-sm text-white/90">
                                        <span className="material-icons text-sm">place</span>
                                        {dest.country}
                                    </p>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${getSeasonColor(dest.bestSeason)}`}>
                                        {t[dest.bestSeason]}
                                    </div>
                                    <div className="flex items-center gap-1 text-amber-500">
                                        <span className="material-icons text-sm">star</span>
                                        <span className="font-bold">{dest.rating}</span>
                                    </div>
                                </div>

                                <div className="flex items-end justify-between mb-6">
                                    <div>
                                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.from}</p>
                                        <p className="text-2xl font-bold text-indigo-500">{formatPrice(dest.avgPrice)}</p>
                                    </div>
                                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.perPerson}</p>
                                </div>

                                <div className="flex flex-wrap gap-1.5">
                                    <button
                                        onClick={() => openModal(dest)}
                                        className={`flex-1 min-w-[80px] py-1.5 px-2 rounded-lg text-xs font-medium transition-all ${isDark
                                            ? 'bg-slate-700 hover:bg-slate-600 text-white'
                                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                            }`}
                                    >
                                        {t.viewDetails}
                                    </button>
                                    <button
                                        onClick={() => handleEditDestination(dest)}
                                        className="px-2 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-all shadow-sm hover:shadow-indigo-500/30"
                                        title={language === 'sv' ? 'Redigera' : 'Edit'}
                                    >
                                        <span className="material-icons text-xs">edit</span>
                                    </button>
                                    <button
                                        onClick={() => handleToggleActive(dest)}
                                        className="px-2 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all shadow-sm hover:shadow-amber-500/30"
                                        title={language === 'sv' ? 'Aktiv/Inaktiv' : 'Toggle Active'}
                                    >
                                        <span className="material-icons text-xs">visibility</span>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteDestination(dest)}
                                        className="px-2 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all shadow-sm hover:shadow-red-500/30"
                                        title={language === 'sv' ? 'Ta bort' : 'Delete'}
                                    >
                                        <span className="material-icons text-xs">delete</span>
                                    </button>
                                    <button
                                        onClick={() => handleToggleLock(dest)}
                                        className={`px-2 py-1.5 rounded-lg transition-all shadow-sm ${(fullDestinations.find((d: any) => d.id === dest.id) as any)?.lockContent
                                            ? 'bg-red-500 hover:bg-red-600 text-white'
                                            : isDark ? 'bg-slate-600 hover:bg-slate-500 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'
                                            }`}
                                        title={language === 'sv' ? 'Kilitle/Aç' : 'Lock/Unlock Content'}
                                    >
                                        <span className="material-icons text-xs">
                                            {(fullDestinations.find((d: any) => d.id === dest.id) as any)?.lockContent ? 'lock' : 'lock_open'}
                                        </span>
                                    </button>
                                    <button
                                        onClick={async () => {
                                            if (enrichingId) return;
                                            setEnrichingId(dest.id);
                                            try {
                                                const result = await enrichDestinationContent(dest.name);
                                                if (result.success) {
                                                    // Find and update the full destination
                                                    const fullDest = fullDestinations.find(d => d.id === dest.id);
                                                    if (fullDest) {
                                                        const enriched: DestinationContent = {
                                                            ...fullDest,
                                                            sections: {
                                                                ...fullDest.sections,
                                                                attractions: result.attractions as any,
                                                                restaurants: result.restaurants as any,
                                                                thingsToDo: result.activities as any
                                                            }
                                                        };
                                                        await handleSaveDestination(enriched);
                                                        alert(language === 'sv' ? `✅ ${dest.name} berikad med ${result.attractions.length} attraktioner!` : `✅ ${dest.name} enriched with ${result.attractions.length} attractions!`);
                                                    }
                                                } else {
                                                    alert(`❌ ${result.error}`);
                                                }
                                            } catch (err: any) {
                                                alert(`❌ Error: ${err.message}`);
                                            } finally {
                                                setEnrichingId(null);
                                            }
                                        }}
                                        disabled={enrichingId !== null}
                                        className={`px-2 py-1.5 rounded-lg transition-all shadow-sm flex items-center gap-0.5 ${enrichingId === dest.id
                                            ? 'bg-purple-400 text-white animate-pulse'
                                            : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white hover:shadow-purple-500/30'
                                            }`}
                                        title={language === 'sv' ? 'AI Berika' : 'AI Enrich'}
                                    >
                                        <span className="material-icons text-xs">{enrichingId === dest.id ? 'hourglass_empty' : 'auto_awesome'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Detail Modal */}
            {showModal && selectedDestination && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
                    <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} shadow-2xl`}>
                        <div className="relative h-64">
                            <img
                                src={selectedDestination.imageUrl}
                                alt={selectedDestination.name}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                            <button
                                onClick={closeModal}
                                className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-all"
                            >
                                <span className="material-icons">close</span>
                            </button>
                            <div className="absolute bottom-6 left-6 text-white">
                                <h2 className="text-4xl font-bold mb-2">{selectedDestination.name}</h2>
                                <div className="flex items-center gap-4 text-sm opacity-90">
                                    <span className="flex items-center gap-1">
                                        <span className="material-icons text-sm">place</span>
                                        {selectedDestination.country}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="material-icons text-sm">thermostat</span>
                                        {selectedDestination.climate}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="p-8">
                            <div className="flex gap-4 mb-8">
                                <button
                                    onClick={() => handleEditDestination(selectedDestination)}
                                    className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold transition-all shadow-lg"
                                >
                                    {t.edit}
                                </button>
                                <button
                                    onClick={() => generateAIContent(selectedDestination)}
                                    disabled={isGeneratingAI}
                                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {isGeneratingAI ? (
                                        <>
                                            <span className="animate-spin material-icons text-sm">refresh</span>
                                            {t.generating}
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-icons text-sm">auto_awesome</span>
                                            {t.generateAI}
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="space-y-8">
                                <div>
                                    <h3 className="text-xl font-bold mb-3">{t.highlights}</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedDestination.highlights.map(h => (
                                            <span key={h} className={`px-3 py-1 rounded-full text-sm ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                                                {h}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-xl font-bold mb-3">{t.travelTips}</h3>
                                    <ul className="space-y-2">
                                        {selectedDestination.travelTips.map(tip => (
                                            <li key={tip} className="flex items-start gap-2">
                                                <span className="material-icons text-green-500 text-sm mt-1">check_circle</span>
                                                <span className="opacity-80">{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {aiContent && (
                                    <div className={`p-6 rounded-2xl ${isDark ? 'bg-purple-900/20 border border-purple-800' : 'bg-purple-50 border border-purple-100'}`}>
                                        <div className="flex items-center gap-2 mb-4 text-purple-600 dark:text-purple-400">
                                            <span className="material-icons">auto_awesome</span>
                                            <h3 className="font-bold">{t.aiGenerated}</h3>
                                        </div>
                                        <div className="prose dark:prose-invert max-w-none text-sm leading-relaxed whitespace-pre-line">
                                            {aiContent}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Video Modal */}
            {showVideoModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowVideoModal(false)} />
                    <div className="relative w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl">
                        <button
                            onClick={() => setShowVideoModal(false)}
                            className="absolute top-4 right-4 z-10 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full backdrop-blur-md transition-all"
                        >
                            <span className="material-icons">close</span>
                        </button>
                        {youtubeUrl ? (
                            <div className="aspect-video bg-black">
                                <iframe
                                    src={getYoutubeEmbedUrl(youtubeUrl)}
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    title="Destination Video"
                                />
                            </div>
                        ) : (
                            <div className={`aspect-video flex flex-col items-center justify-center ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-white text-slate-600'}`}>
                                <span className="material-icons text-6xl mb-4 text-slate-400">videocam_off</span>
                                <p className="text-lg font-medium">{language === 'sv' ? 'Ingen video konfigurerad' : 'No video configured'}</p>
                                <p className="text-sm mt-2 opacity-70">{language === 'sv' ? 'Ange en YouTube-URL under Inställningar → DESTINATION_VIDEO_URL' : 'Set a YouTube URL in Settings → DESTINATION_VIDEO_URL'}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Category Content Panel */}
            {activeCategory && (
                <div className="fixed inset-0 z-[55] flex items-end justify-center">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setActiveCategory(null); setBlogPreview(null); setBlogMessage(null); }} />
                    <div className={`relative w-full max-w-5xl max-h-[80vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} shadow-2xl`}>
                        {/* Panel Header */}
                        <div className="sticky top-0 z-10 px-8 py-5 flex items-center justify-between border-b backdrop-blur-md" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', backgroundColor: isDark ? 'rgba(30,41,59,0.95)' : 'rgba(255,255,255,0.95)' }}>
                            <div className="flex items-center gap-3">
                                <span className={`material-icons text-2xl p-2 rounded-xl bg-gradient-to-br ${categories.find(c => c.id === activeCategory)?.color || 'from-blue-500 to-indigo-600'} text-white`}>
                                    {categories.find(c => c.id === activeCategory)?.icon || 'category'}
                                </span>
                                <div>
                                    <h3 className="text-xl font-bold">
                                        {categories.find(c => c.id === activeCategory)?.label || activeCategory}
                                    </h3>
                                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {language === 'sv' ? 'Generera blogginnehåll för dina destinationer' : 'Generate blog content for your destinations'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setActiveCategory(null); setBlogPreview(null); setBlogMessage(null); }}
                                className={`p-2 rounded-xl transition-all ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                            >
                                <span className="material-icons">close</span>
                            </button>
                        </div>

                        {/* Blog Message */}
                        {blogMessage && (
                            <div className={`mx-8 mt-4 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ${blogMessage.type === 'success'
                                ? (isDark ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-green-100 text-green-700')
                                : (isDark ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-red-100 text-red-700')}`}>
                                <span className="material-icons text-sm">{blogMessage.type === 'success' ? 'check_circle' : 'error'}</span>
                                {blogMessage.text}
                            </div>
                        )}

                        {/* Blog Preview */}
                        {blogPreview && (
                            <div className={`mx-8 mt-4 p-6 rounded-2xl border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-lg font-bold flex items-center gap-2">
                                        <span className="material-icons text-purple-500">auto_awesome</span>
                                        {language === 'sv' ? 'AI-genererat bloggförslag' : 'AI-Generated Blog Preview'}
                                    </h4>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleSaveBlogPost('draft')}
                                            disabled={savingBlog}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${isDark ? 'bg-slate-600 text-white hover:bg-slate-500' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'} disabled:opacity-50`}
                                        >
                                            {savingBlog ? '...' : (language === 'sv' ? '📝 Spara utkast' : '📝 Save Draft')}
                                        </button>
                                        <button
                                            onClick={() => handleSaveBlogPost('published')}
                                            disabled={savingBlog}
                                            className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50"
                                        >
                                            {savingBlog ? '...' : (language === 'sv' ? '🚀 Publicera' : '🚀 Publish')}
                                        </button>
                                        <button
                                            onClick={() => setBlogPreview(null)}
                                            className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            <span className="material-icons text-sm">close</span>
                                        </button>
                                    </div>
                                </div>
                                {blogPreview.coverImage && (
                                    <div className="mb-4 rounded-xl overflow-hidden h-48">
                                        <img src={blogPreview.coverImage} alt="Cover" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <p className="text-xs text-purple-500 font-medium mb-1">{blogPreview.destinationName} — {activeCategory}</p>
                                <h5 className="text-xl font-bold mb-2">{language === 'sv' ? blogPreview.title?.sv : blogPreview.title?.en}</h5>
                                <p className={`text-sm mb-4 italic ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                                    {language === 'sv' ? blogPreview.excerpt?.sv : blogPreview.excerpt?.en}
                                </p>
                                <div className={`text-sm leading-relaxed whitespace-pre-line max-h-60 overflow-y-auto pr-2 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                                    {language === 'sv' ? blogPreview.content?.sv : blogPreview.content?.en}
                                </div>
                                {blogPreview.tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {blogPreview.tags.map((tag: string) => (
                                            <span key={tag} className={`px-2 py-0.5 rounded-full text-xs ${isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>{tag}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Destination Cards */}
                        <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {destinations.map(dest => {
                                const fullDest = fullDestinations.find((d: any) => d.id === dest.id);
                                const destId = (fullDest as any)?._id || dest.id;
                                return (
                                    <div
                                        key={dest.id}
                                        className={`rounded-2xl overflow-hidden transition-all ${isDark ? 'bg-slate-700/70 border border-slate-600' : 'bg-white border border-slate-200 shadow-sm'}`}
                                    >
                                        <div className="relative h-32">
                                            <img src={dest.imageUrl} alt={dest.name} className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                            <div className="absolute bottom-3 left-3 text-white">
                                                <h4 className="font-bold text-lg">{dest.name}</h4>
                                                <p className="text-xs opacity-80">{dest.country}</p>
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            <button
                                                onClick={() => handleGenerateBlog(destId)}
                                                disabled={generatingBlogFor !== null}
                                                className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${generatingBlogFor === destId
                                                    ? 'bg-purple-500 text-white animate-pulse'
                                                    : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-sm hover:shadow-lg'
                                                    } disabled:opacity-50`}
                                            >
                                                <span className="material-icons text-sm">
                                                    {generatingBlogFor === destId ? 'hourglass_empty' : 'auto_awesome'}
                                                </span>
                                                {generatingBlogFor === destId
                                                    ? (language === 'sv' ? 'Genererar...' : 'Generating...')
                                                    : (language === 'sv' ? 'Generera blogginnehåll' : 'Generate Blog Content')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DestinationsContainer;
